import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Mail, Loader2, Copy, RefreshCw, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/contexts/AuthContext';

const RESULTATS = [
  { value: 'decouverte', label: '🔍 Découverte effectuée' },
  { value: 'rdv_confirme', label: '📅 RDV confirmé' },
  { value: 'relance', label: '🔁 Relance demandée' },
  { value: 'devis', label: '📄 Devis à envoyer' },
];

export default function PostCallEmailDialog({ prospectId, prospectName, prospectEmail, trigger }) {
  const [open, setOpen] = useState(false);
  const [resultat, setResultat] = useState('decouverte');
  const [callNotes, setCallNotes] = useState('');
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true); setHtml('');
    try {
      const { data } = await api.post('/ai/post-call-email', { prospect_id: prospectId, resultat, call_notes: callNotes });
      setHtml(data.html);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur génération");
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const plainText = temp.innerText;
    navigator.clipboard.writeText(plainText);
    toast.success("Email copié (texte brut)");
  };

  const openInMailClient = () => {
    if (!prospectEmail) { toast.error("Email du prospect manquant"); return; }
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const plainText = temp.innerText;
    const subject = resultat === 'rdv_confirme' ? 'Confirmation de notre RDV - WASABI Corner' : 'Suite à notre échange - WASABI Corner';
    window.open(`mailto:${prospectEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(plainText)}`, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div onClick={() => setOpen(true)}>{trigger}</div>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="post-call-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#F97316]" />Email post-appel IA
          </DialogTitle>
          <DialogDescription className="text-xs">
            Email professionnel généré par Claude pour <strong>{prospectName}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <Label className="text-xs">Résultat de l'appel</Label>
            <Select value={resultat} onValueChange={setResultat}>
              <SelectTrigger data-testid="email-resultat-select"><SelectValue /></SelectTrigger>
              <SelectContent>{RESULTATS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="self-end">
            <Button onClick={generate} disabled={loading} className="bg-[#F97316] hover:bg-[#EA580C] w-full" data-testid="generate-email-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
              {html ? 'Regénérer' : 'Générer'}
            </Button>
          </div>
        </div>

        <div className="mt-2">
          <Label className="text-xs">Notes de l'appel (optionnel)</Label>
          <Textarea value={callNotes} onChange={e => setCallNotes(e.target.value)} rows={2}
            placeholder="Ex: Client intéressé par offre marges, souhaite démo produit..."
            className="text-xs" data-testid="email-notes-textarea" />
        </div>

        <div className="flex-1 overflow-y-auto mt-3 p-4 bg-[#FAFAFA] border border-[#E5E7EB] rounded-lg min-h-[220px]" data-testid="email-output">
          {loading && (
            <div className="flex flex-col items-center justify-center h-full text-[#71717A]">
              <Loader2 className="w-8 h-8 animate-spin mb-2 text-[#F97316]" />
              <p className="text-xs">Claude rédige votre email…</p>
            </div>
          )}
          {!loading && !html && <p className="text-xs text-[#71717A] text-center pt-8">Choisissez le résultat et cliquez sur <strong>Générer</strong></p>}
          {!loading && html && <div className="prose prose-sm max-w-none text-sm" dangerouslySetInnerHTML={{ __html: html }} />}
        </div>

        <DialogFooter className="gap-2">
          {html && (
            <>
              <Button variant="outline" size="sm" onClick={copy} data-testid="copy-email-btn"><Copy className="w-3 h-3 mr-1" />Copier</Button>
              <Button size="sm" onClick={openInMailClient} disabled={!prospectEmail} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="send-email-btn">
                <Send className="w-3 h-3 mr-1" />Ouvrir dans mon client mail
              </Button>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
